import styles from './SettingsPage.module.scss'
import { useLocation, useNavigate } from "react-router-dom"
import useHasRate from "../../hooks/useHasRate"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { EMPLOYMENTS, EXPERIENCES, RATES_URL, SOURCES } from "../../utils/consts"
import Layout from '../../ui/Layout/Layout'
import Autocomplete from '../../ui/Autocomplete/Autocomplete'
import useGetCities from '../../hooks/useGetCities'
import TextInput from '../../ui/TextInput/TextInput'
import LoadingModal from '../../ui/LoadingModal/LoadingModal'
import Button from '../../ui/Button/Button'
import Tag from '../../ui/Tag/Tag'
import { useDebounce } from '../../hooks/common'
import { useGetFilters, useUpdateFilters } from '../../hooks/filters'
import useGetCompanies from '../../hooks/useGetCompanies'
import { IOption } from '../../utils'
import { useIsAuth } from '../../hooks/auth'
import CheckInput from '../../ui/CheckInput/CheckInput'
import MultipleAutocomplete from '../../ui/MultipleAutocomplete/MultipleAutocomplete'
import useGetUser from '../../hooks/useGetUser'
import { Tooltip } from '@mui/material'


const SettingsPage = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const hash = useMemo(() => {
    return location.hash
  }, [location])

  const copyIdBtnRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (hash === "#copy-id-btn") {
      copyIdBtnRef.current?.scrollIntoView()
    }
  }, [hash, copyIdBtnRef.current])

  const user = useGetUser()

  const cities = useGetCities() || []
  const allCompanies = useGetCompanies() || []
  
  const {data: isAuth} = useIsAuth()
  const {data: filters, isPending, isFetchedAfterMount} = useGetFilters()
  const updateFilters = useUpdateFilters()
  useEffect(() => {
    if (isAuth === false) {
      navigate(`/auth?redirectAfter=filters${hash || ''}`)
    }
  }, [navigate, isAuth])

  const [city, setCity] = useState(filters?.city)
  const [employments, setEmployments] = useState(filters?.employments)
  const [experience, setExperience] = useState(filters?.experience)
  const [keyWords, setKeyWords] = useState(filters?.keyWords || [])
  const [companies, setCompanies] = useState(filters?.companies || [])
  const [sources, setSources] = useState(filters?.sources || [])
  const [filterByCompanies, setFilterByCompanies] = useState(filters?.filterByCompanies || false)

  const [keyWord, setKeyWord] = useState('')

  useEffect(() => {
    if (isFetchedAfterMount && filters) {
      setCity(filters.city)
      setEmployments(filters.employments)
      setExperience(filters.experience)
      setKeyWords(filters.keyWords)
      setCompanies(filters.companies)
      setFilterByCompanies(filters.filterByCompanies)
    }
  }, [isFetchedAfterMount])

  const debounceCb = useCallback(async () => {
    updateFilters({
      city, employments, experience, keyWords: keyWords || [], companies: companies || [], sources: sources || [], filterByCompanies: filterByCompanies
    })
  }, [city, employments, experience, keyWords, companies, sources, filterByCompanies])

  const [isInitial, setIsinitial] = useState(true)
  useDebounce(async () => {
    if (!isInitial) {
      await debounceCb()
    } else {
      setIsinitial(false)
    }
  }, [debounceCb], 500)

  const addKeyWord = (item: string) => {
    if (!item) { return; }
    if (!keyWords.includes(item)) {
      setKeyWords(cur => [...cur, item])
      setKeyWord('')
    }
  }

  const addCompany = (item: IOption<string>) => {
    if (!companies.includes(item.value)) {
      setCompanies(cur => [...cur, item.value])
    }
  }

  const setSource = (source: string, value: boolean) => {
    if (value) {
      setSources(src => [...src, source])
    } else {
      setSources(src => src.filter(item => item !== source))
    }
  }

  if (isPending ||
    // inProgress ||
    experience === undefined ||
    employments === undefined 
  ) {
    return <LoadingModal />
  }

  return (
    <div className={styles.root}>
      <h1>Фильтры</h1>
      <Layout className={styles.baseParams} kind='white'>
        <Autocomplete 
          label='Город' 
          options={cities} 
          value={city || { value: '', label: 'Все' }} 
          setValue={(val) => {setCity(val);}} 
        />
        <MultipleAutocomplete label='Занятость' options={EMPLOYMENTS} value={employments} setValue={val => {setEmployments(val);}} />
        <Autocomplete label='Опыт работы' options={EXPERIENCES} value={experience} setValue={val => {setExperience(val);}} />
      </Layout>

      <Layout className={styles.tagsContainer} kind='white'>
        <div className={styles.input}>
          <TextInput
            label='Ключевые слова'
            value={keyWord}
            setValue={setKeyWord}
            onEnter={() => { addKeyWord(keyWord) }}
          />
          <Button
            onClick={() => { addKeyWord(keyWord) }}
            kind='black'
          >
            <p>Добавить</p>
          </Button>
        </div>
        <p>Ваши вакансии будут содержать:</p>
        <div className={styles.tags}>
          {keyWords?.map(tag => (
            <Tag tag={tag} onRemove={(tag) => { setKeyWords(cur => cur.filter(item => item !== tag)) }} kind='red' />
          ))}
          {!keyWords?.length && <p className={styles.empty}>Нет ключевых слов</p>}
        </div>
      </Layout>

      <Layout className={styles.sources} kind='white'>
        <div className={styles.sourcesContainer}>
          {SOURCES.map(source => (
            <span>
              <p>{source.name}</p>
              <CheckInput checked={sources.includes(source.id)} setValue={val => {setSource(source.id, val)}}/>
            </span>
          ))}
        </div>
      </Layout>

      <Layout className={styles.tagsContainer} kind='white'>
        <span className={styles.showOnlyChosen}>
          <CheckInput
            checked={filterByCompanies}
            setValue={setFilterByCompanies}
          />
          <p>Показывать только вакансии избранных компаний</p>
        </span>
        <Autocomplete 
          className={styles.companiesInput}
          key={companies.length}
          label='Избранные компании' 
          options={allCompanies} 
          value={{value: '', label: ''}}
          setValue={val => {
            addCompany(val)
          }}
        />
        <p>Вы будете получать уведомления о вакансиях компаний:</p>
        <div className={styles.tags}>
          {companies?.map(tag => (
            <Tag tag={tag} onRemove={(tag) => { setCompanies(cur => cur.filter(item => item !== tag)) }} kind='red' />
          ))}
          {!companies?.length && <p className={styles.empty}>Нет компаний</p>}
        </div>

        <Tooltip
          title="Используется для привзяки телеграм бота к аккаунту"
        >
          <span style={{width: '100%'}} ref={copyIdBtnRef}>
            <Button
              id="copy-id-btn"
              onClick={() => {
                const textArea = document.createElement("textarea");
                textArea.value = `id:${user?.accauntId}`;
                document.body.appendChild(textArea);
                textArea.select();
                try {
                  document.execCommand('copy');
                } catch (err) {
                  console.error('Unable to copy to clipboard', err);
                }
                document.body.removeChild(textArea);

                // navigator.clipboard.writeText()
              }}
              kind="black"
              style={{width: '100%'}}
            >
              Копировать id
            </Button>
          </span>
        </Tooltip>
      </Layout>

    </div>
  )
}

export default SettingsPage